import {
  createElement,
  forwardRef,
  useState,
  useRef,
  useReducer,
  useImperativeHandle,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import cx from 'classnames';
import { DatePickerProps, DatePickerRef } from './types';
import { DatePickerPopover } from './DatePickerPopover';
import { Button } from '../Button';
import { Input, InputChangeEvent, InputCommitEvent } from '../Input';
import { BaseInput } from '../Input/__internal__/BaseInput';
import { validateTextInput, useInputValidationAdapter } from '../Input/useInputValidationAdapter';
import {
  getMonthAndYearByDate,
  isSameDay,
  getYearOrder,
  getDateByWeekNumber,
  getDayPeriodOrder,
  getDayPeriods,
  getTimeSeparator,
} from './DateUtil';
import { InvisibleText } from '../InvisibleText';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useMounted } from '../../hooks/useMounted';
import { assertNever } from '@sas/nova-core/typeguards';
import { Date as SvgDate, DateTime as SvgDateTime, Time as SvgTime } from '@sas/react-svg';
import { SVGIconDescriptor } from '../SVG';
import { isPastDate, isAfterDate } from '../Calendar/DisabledDayFilters';
import { useDefaultDateTimeFormat } from './useDefaultDateTimeFormat';
import { PickerRef } from './Pickers/PickerRef';
import { postfixTestId } from '../../util/testId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { withLabels } from '../withLabels';
import bundle from './NovaComponents-DatePicker-gui-icu.properties';

import './DatePicker.css';
import cn from './DatePicker.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('DatePicker');
const useValidationMessageId = createUseHtmlId('DatePicker-ValidationMessage');

function hasTimePickerProps(x: {
  type?: DatePickerProps['type'];
}): x is { type: 'time' | 'datetime' } {
  return x.type === 'time' || x.type === 'datetime';
}

export const DatePicker = forwardRef<DatePickerRef, DatePickerProps>(function DatePicker(
  props,
  ref
) {
  const defaultFormat = useDefaultDateTimeFormat({
    type: props.type ?? 'date',
    showMilliseconds: hasTimePickerProps(props) && props.showMilliseconds,
    showHours: hasTimePickerProps(props) && props.showHours,
    showSeconds: hasTimePickerProps(props) && props.showSeconds,
    showDayPeriod: hasTimePickerProps(props) && props.showDayPeriod,
    showHoursDuration: hasTimePickerProps(props) && props.showHoursDuration,
    showMinutesDuration: hasTimePickerProps(props) && props.showMinutesDuration,
  });

  const textValidator = useInputValidationAdapter({
    isRequired: true,
    initialValue: props.value ? (props.format ?? defaultFormat)(props.value) : '',
  });

  const refPicker = useRef<PickerRef>(null);
  const type = props.type ?? 'date';

  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const mountedRef = useMounted();

  const defaultMinDate = useMemo<Date>(() => new Date(1582, 0, 1), []);
  const defaultMaxDate = useMemo<Date>(() => new Date(9999, 11, 31), []);

  const minDate = useMemo<Date>(() => {
    if (
      props.minDate &&
      isAfterDate(props.minDate, { today: defaultMinDate }) &&
      (isPastDate(props.minDate, { today: defaultMaxDate }) ||
        isSameDay(props.minDate, defaultMaxDate))
    ) {
      const mnDate = new Date(props.minDate);
      if (props.type === 'datetime' && !props.showMilliseconds) mnDate.setMilliseconds(0);
      if (props.type === 'month') {
        // Set to 1st day of month
        mnDate.setDate(1);
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      } else if (props.type === 'quarter') {
        // Set minDate to beginning of the quarter
        mnDate.setDate(1);
        if (mnDate.getMonth() >= 0 && mnDate.getMonth() < 3) {
          mnDate.setMonth(0);
        } else if (mnDate.getMonth() >= 3 && mnDate.getMonth() < 6) {
          mnDate.setMonth(3);
        } else if (mnDate.getMonth() >= 6 && mnDate.getMonth() < 9) {
          mnDate.setMonth(6);
        } else if (mnDate.getMonth() >= 9) {
          mnDate.setMonth(9);
        }
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      }
      return mnDate;
    } else return new Date(defaultMinDate);
  }, [defaultMaxDate, defaultMinDate, props.minDate, props.showMilliseconds, props.type]);

  const maxDate = useMemo<Date>(() => {
    if (
      props.maxDate &&
      (isAfterDate(props.maxDate, { today: defaultMinDate }) ||
        isSameDay(props.maxDate, defaultMinDate)) &&
      isPastDate(props.maxDate, { today: defaultMaxDate })
    ) {
      const mxDate = new Date(props.maxDate);
      if (props.type === 'datetime' && !props.showMilliseconds) mxDate.setMilliseconds(0);
      if (props.type === 'month') {
        // Set to last day of month
        mxDate.setDate(new Date(mxDate.getFullYear(), mxDate.getMonth() + 1, 0).getDate());
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      } else if (props.type === 'quarter') {
        // Set maxDate to end of the quarter
        mxDate.setDate(1);
        if (mxDate.getMonth() >= 0 && mxDate.getMonth() < 3) {
          mxDate.setMonth(2);
        } else if (mxDate.getMonth() >= 3 && mxDate.getMonth() < 6) {
          mxDate.setMonth(5);
        } else if (mxDate.getMonth() >= 6 && mxDate.getMonth() < 9) {
          mxDate.setMonth(8);
        } else if (mxDate.getMonth() >= 9) {
          mxDate.setMonth(11);
        }
        mxDate.setDate(new Date(mxDate.getFullYear(), mxDate.getMonth() + 1, 0).getDate());
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      }
      return mxDate;
    } else return new Date(defaultMaxDate);
  }, [defaultMaxDate, defaultMinDate, props.maxDate, props.showMilliseconds, props.type]);

  const isBeforeMinDate = useCallback((day: Date) => isPastDate(day, { today: minDate }), [
    minDate,
  ]);
  const isAfterMaxDate = useCallback((day: Date) => isAfterDate(day, { today: maxDate }), [
    maxDate,
  ]);

  /**
   * Possible values:
   * - **unfocused**: the user isn't editing the value
   * - **editing**: the user is editing the value
   * - **committed**: the user just committed a value but is still editing.
   */
  type EditState = 'committed' | 'editing' | 'unfocused';
  const [editState, setEditState] = useState<EditState>('unfocused');

  // For use when editable and/or required
  const [value, setValue] = useState(
    props.value ? (props.format ?? defaultFormat)(props.value) : ''
  );

  const [state, setState] = useState(props.value ?? new Date());
  {
    const [prevValue, setPrevValue] = useState(props.value);
    if ((!!prevValue ? prevValue.getTime() : 0) !== (!!props.value ? props.value.getTime() : 0)) {
      setPrevValue(props.value);
      setState(props.value ?? new Date());
      let newValue = '';
      if (props.editable) {
        if (props.value === null) {
          setValue('');
          newValue = '';
        } else if (editState === 'editing') {
          setValue(value);
          newValue = value;
        } else {
          newValue = (props.format ?? defaultFormat)(props.value);
          setValue(newValue);
        }
      } else {
        if (props.value === null) {
          setValue('');
          newValue = '';
        } else {
          newValue = (props.format ?? defaultFormat)(props.value);
          setValue(newValue);
        }
      }
      const changeEvent: InputChangeEvent = { value: newValue };
      textValidator.handleChange(changeEvent);
    }
  }

  let buttonIcon: SVGIconDescriptor;
  let roleDescription = localize('date.role.aria');
  let iconTooltip = localize('date.button.tooltip.label');
  switch (props.type) {
    case null:
    case undefined:
    case 'date':
      buttonIcon = SvgDate;
      iconTooltip = localize('date.button.tooltip.label');
      roleDescription = localize('date.role.aria');
      break;
    case 'datetime':
      buttonIcon = SvgDateTime;
      iconTooltip = localize('datetime.button.tooltip.label');
      roleDescription = localize('datetime.role.aria');
      break;
    case 'month':
      buttonIcon = SvgDate;
      iconTooltip = localize('month.button.tooltip.label');
      roleDescription = localize('month.role.aria');
      break;
    case 'quarter':
      buttonIcon = SvgDate;
      iconTooltip = localize('quarter.button.tooltip.label');
      roleDescription = localize('quarter.role.aria');
      break;
    case 'week':
      buttonIcon = SvgDate;
      iconTooltip = localize('week.button.tooltip.label');
      roleDescription = localize('week.role.aria');
      break;
    case 'time':
      buttonIcon = SvgTime;
      iconTooltip = localize('time.button.tooltip.label');
      roleDescription = localize('time.role.aria');
      break;
    default:
      assertNever(props);
  }

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [hidePicker, setHidePicker] = useState(true);
  const [isValid, setIsValid] = useState(true);
  const [isMinMaxValid, setIsMinMaxValid] = useState(true);

  const getParsedValue = useCallback(
    (value: string): string => {
      const dateValue = new Date();
      if (props.type === 'quarter' || props.type === 'week') {
        const yearOrder = getYearOrder(locale);
        const parts = value.split(' ');
        const year = yearOrder === 'after' ? parts[1] : parts[0];
        const dtVal = yearOrder === 'after' ? parts[0].substring(1) : parts[1].substring(1);
        dateValue.setDate(1);
        if (props.type === 'quarter') {
          if (dtVal === '1') {
            dateValue.setMonth(0);
          } else if (dtVal === '2') {
            dateValue.setMonth(3);
          } else if (dtVal === '3') {
            dateValue.setMonth(6);
          } else if (dtVal === '4') {
            dateValue.setMonth(9);
          }
          dateValue.setFullYear(parseInt(year));
          return dateValue.toString();
        } else if (props.type === 'week') {
          return getDateByWeekNumber(parseInt(dtVal), parseInt(year)).toString();
        }
      } else if (props.type === 'time') {
        const dayPeriodOrder = getDayPeriodOrder(locale);
        const h24clock = dayPeriodOrder === 'none';
        const dayPeriods = getDayPeriods(locale);
        const timeSeparator = getTimeSeparator(locale);
        const timeParts = value.split(timeSeparator);
        const ampm = h24clock
          ? null
          : dayPeriodOrder === 'before'
          ? timeParts[0].split(' ')[0]
          : timeParts[2].split(' ')[1];
        let hours = parseInt(
          dayPeriodOrder === 'before' ? timeParts[0].split(' ')[1] : timeParts[0]
        );
        const seconds = parseInt(
          dayPeriodOrder === 'after' ? timeParts[2].split(' ')[0] : timeParts[2]
        );
        const milliSeconds = props.showMilliseconds
          ? parseInt(
              dayPeriodOrder === 'after'
                ? timeParts[2].split('.')[1].split(' ')[0]
                : timeParts[2].split('.')[1]
            )
          : 0;
        if (ampm && ampm === dayPeriods[0] && hours === 12) {
          hours = 0;
        } else if (ampm && ampm === dayPeriods[1] && hours !== 12) {
          hours += 12;
        }
        dateValue.setHours(hours);
        dateValue.setMinutes(parseInt(timeParts[1]));
        dateValue.setSeconds(seconds);
        dateValue.setMilliseconds(milliSeconds);
        return dateValue.toJSON();
      }

      return '';
    },
    [locale, props.showMilliseconds, props.type]
  );

  const handleChange = (e: InputChangeEvent) => {
    if (!mountedRef.current) return;
    setValue(e.value);
    if (props.editable) {
      textValidator.handleChange(e);
      setEditState((prevState) => (prevState !== 'unfocused' ? 'editing' : prevState));
      if (e.value === '') props.onChange({ value: null });
    } else if (e.value === '') {
      inputRef.current?.focus();
      setState(new Date());
      setIsMinMaxValid(true);
      props.onChange({ value: null });
    }
  };

  const handleCommit = useCallback(
    (e: InputCommitEvent) => {
      if (!mountedRef.current) return;
      const valueToCommit =
        props.editable &&
        (props.type === 'quarter' || props.type === 'week' || props.type === 'time')
          ? getParsedValue(e.value)
          : e.value;
      let d = new Date(valueToCommit);
      if (!isNaN(d.getTime())) {
        if (isBeforeMinDate(d) || isAfterMaxDate(d)) {
          setIsMinMaxValid(false);
          setIsValid(false);
          d = new Date();
        } else {
          setIsMinMaxValid(true);
          setIsValid(true);
        }
        setState(d);
        props.onChange({ value: d });
      } else {
        if (props.required && e.value === '') {
          setIsValid(false);
        } else {
          setIsMinMaxValid(true);
          setIsValid(true);
        }
      }
      setEditState((prevState) => (prevState === 'editing' ? 'committed' : prevState));
    },
    [getParsedValue, isAfterMaxDate, isBeforeMinDate, mountedRef, props]
  );

  const handleBlur = () => {
    if (!mountedRef.current) return;
    if (props.required) {
      const result = validateTextInput({ value, isRequired: true });
      setIsValid(!result.isError);
    } else if (props.status) {
      setIsValid(false);
    } else setIsValid(true);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus() {
      inputRef.current?.focus();
    },
  }));

  const refFocusTimeout = useRef<number | undefined>(undefined);

  type FocusAction =
    | { type: 'dayStep'; direction: 'previous' | 'next' }
    | { type: 'monthStep'; direction: 'previous' | 'next' }
    | { type: 'yearStep'; direction: 'previous' | 'next' }
    | { type: 'hourStep'; direction: 'previous' | 'next' }
    | { type: 'minuteStep'; direction: 'previous' | 'next' }
    | { type: 'secondStep'; direction: 'previous' | 'next' }
    | { type: 'selection'; value: Date };
  const [focusState, dispatchFocusAction] = useReducer((prevState: Date, action: FocusAction) => {
    switch (action.type) {
      case 'dayStep': {
        const nextDate = new Date(prevState);
        if (action.direction === 'next') {
          nextDate.setDate(nextDate.getDate() + 1);
        } else if (action.direction === 'previous') {
          nextDate.setDate(nextDate.getDate() - 1);
        }
        if (isBeforeMinDate(nextDate) || isAfterMaxDate(nextDate)) return new Date(prevState);
        props.onChange({ value: nextDate });
        return nextDate;
      }
      case 'monthStep': {
        const nextDate = new Date(prevState);
        if (action.direction === 'next') {
          nextDate.setMonth(nextDate.getMonth() + 1);
        } else if (action.direction === 'previous') {
          nextDate.setMonth(nextDate.getMonth() - 1);
        }
        if (isBeforeMinDate(nextDate) || isAfterMaxDate(nextDate)) return new Date(prevState);
        props.onChange({ value: nextDate });
        return nextDate;
      }
      case 'yearStep': {
        const nextDate = new Date(prevState);
        if (action.direction === 'next') {
          nextDate.setFullYear(nextDate.getFullYear() + 1);
        } else if (action.direction === 'previous') {
          nextDate.setFullYear(nextDate.getFullYear() - 1);
        }
        if (isBeforeMinDate(nextDate) || isAfterMaxDate(nextDate)) return new Date(prevState);
        props.onChange({ value: nextDate });
        return nextDate;
      }
      case 'hourStep': {
        const nextDate = new Date(prevState);
        if (action.direction === 'next') {
          nextDate.setHours(nextDate.getHours() + 1);
        } else if (action.direction === 'previous') {
          nextDate.setHours(nextDate.getHours() - 1);
        }
        props.onChange({ value: nextDate });
        return nextDate;
      }
      case 'minuteStep': {
        const nextDate = new Date(prevState);
        if (action.direction === 'next') {
          nextDate.setMinutes(nextDate.getMinutes() + 1);
        } else if (action.direction === 'previous') {
          nextDate.setMinutes(nextDate.getMinutes() - 1);
        }
        props.onChange({ value: nextDate });
        return nextDate;
      }
      case 'secondStep': {
        const nextDate = new Date(prevState);
        if (action.direction === 'next') {
          nextDate.setSeconds(nextDate.getSeconds() + 1);
        } else if (action.direction === 'previous') {
          nextDate.setSeconds(nextDate.getSeconds() - 1);
        }
        props.onChange({ value: nextDate });
        return nextDate;
      }
      case 'selection':
        return action.value;
      default:
        return prevState;
    }
  }, state);

  useEffect(() => {
    if (
      (props.value && isPastDate(new Date(props.value), { today: minDate })) ||
      (props.value && isAfterDate(new Date(props.value), { today: maxDate }))
    ) {
      setIsMinMaxValid(false);
    } else {
      setIsMinMaxValid(true);
    }

    const handleMouseUp = () => {
      if (!props.readOnly && !props.editable) setHidePicker((prev) => !prev);
    };

    const handleFocusOut = () => {
      if (!mountedRef.current) return;
      props.required && textValidator.handleBlur();
      setTimeout(() => setEditState('unfocused'), 0);
    };

    const handleInputKeyDown = (e: KeyboardEvent) => {
      if (props.readOnly || props.disabled) return;
      if (props.editable)
        setEditState((prevState) => (prevState !== 'committed' ? 'editing' : prevState));
      const key = e.key;
      const alt = e.altKey;
      const ctrl = e.ctrlKey;
      const shift = e.shiftKey;
      switch (key) {
        case ' ':
          if (!props.editable) setHidePicker((prev) => !prev);
          break;
        case 'Spacebar':
          if (!props.editable) setHidePicker((prev) => !prev);
          break;
        case 'Enter':
          if (!props.editable) setHidePicker((prev) => !prev);
          else {
            const commitEvent: InputCommitEvent = { value };
            handleCommit(commitEvent);
          }
          break;
        case 'ArrowUp':
          if (alt) setHidePicker((prev) => !prev);
          break;
        case 'ArrowDown':
          if (alt) setHidePicker((prev) => !prev);
          break;
        case 'PageUp':
          if ((e.target as HTMLInputElement).value === '') return;
          if (ctrl && shift && props.type === 'time')
            dispatchFocusAction({ type: 'secondStep', direction: 'previous' });
          else if (ctrl && shift) dispatchFocusAction({ type: 'yearStep', direction: 'previous' });
          else if (shift && props.type === 'time')
            dispatchFocusAction({ type: 'minuteStep', direction: 'previous' });
          else if (shift) dispatchFocusAction({ type: 'monthStep', direction: 'previous' });
          else if (props.type === 'time')
            dispatchFocusAction({ type: 'hourStep', direction: 'previous' });
          else dispatchFocusAction({ type: 'dayStep', direction: 'previous' });
          e.preventDefault();
          break;
        case 'PageDown':
          if ((e.target as HTMLInputElement).value === '') return;
          if (ctrl && shift && props.type === 'time')
            dispatchFocusAction({ type: 'secondStep', direction: 'next' });
          else if (ctrl && shift) dispatchFocusAction({ type: 'yearStep', direction: 'next' });
          else if (shift && props.type === 'time')
            dispatchFocusAction({ type: 'minuteStep', direction: 'next' });
          else if (shift) dispatchFocusAction({ type: 'monthStep', direction: 'next' });
          else if (props.type === 'time')
            dispatchFocusAction({ type: 'hourStep', direction: 'next' });
          else dispatchFocusAction({ type: 'dayStep', direction: 'next' });
          e.preventDefault();
          break;
        case 'Escape':
          props.onCancel?.();
          break;
        case 'Delete':
        case 'Backspace':
          if (!props.enableResetButton && !props.editable) e.preventDefault();
          break;
        default:
          if (!props.editable || editState !== 'editing') {
            if ((e.target as HTMLInputElement).value === '') return;
            if (state !== props.value) props.onChange({ value: new Date(state) });
          }
          break;
      }
    };

    const input = inputRef.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    input?.addEventListener('mouseup', handleMouseUp);
    input?.addEventListener('focusout', handleFocusOut);
    if (!props.editable) inputRef.current?.setAttribute('aria-readonly', 'true');
    return function () {
      input?.removeEventListener('keydown', handleInputKeyDown);
      input?.removeEventListener('mouseup', handleMouseUp);
      input?.removeEventListener('focusout', handleFocusOut);
    };
  }, [
    props,
    focusState,
    state,
    value,
    defaultFormat,
    minDate,
    maxDate,
    textValidator,
    handleCommit,
    editState,
    mountedRef,
  ]);

  const idValidationMessage = useValidationMessageId();
  const invisibleTextId = useHtmlId();
  const ariaDescribedBy: string[] = [];
  if (!props.disabled) {
    ariaDescribedBy.push(invisibleTextId);
    if (!props.editable) ariaDescribedBy.push(idValidationMessage);
  }
  if (props.ariaDescribedBy) {
    if (typeof props.ariaDescribedBy === 'string') {
      ariaDescribedBy.push(props.ariaDescribedBy);
    } else {
      ariaDescribedBy.push(...props.ariaDescribedBy);
    }
  }

  const printMinMaxErrorMessage = useMemo(() => {
    const minMaxMonthErrorMsg = localize('minmax.month.error.msg', {
      min: getMonthAndYearByDate(locale, minDate),
      max: getMonthAndYearByDate(locale, maxDate),
    });

    const minMaxQuarterErrorMsg = localize('minmax.quarter.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate),
    });

    const minMaxDateErrorMsg = localize('minmax.date.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate),
    });

    const minMaxDateTimeErrorMsg = localize('minmax.datetime.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate),
    });
    switch (props.type) {
      case 'month':
        return minMaxMonthErrorMsg;
      case 'quarter':
        return minMaxQuarterErrorMsg;
      case 'datetime':
        return minMaxDateTimeErrorMsg;
      default:
        return minMaxDateErrorMsg;
    }
  }, [defaultFormat, locale, localize, maxDate, minDate, props.format, props.type]);

  const isDisabledDate = (day: Date) => {
    if (props.showHoursDuration || props.showMinutesDuration) return false;
    if (isBeforeMinDate(day) || isAfterMaxDate(day)) return true;

    if (props.disabledDates) {
      return props.disabledDates.some((disabledDate) => isSameDay(disabledDate, day));
    }

    if (props.enabledDates) {
      return props.enabledDates.every((enabledDate) => !isSameDay(enabledDate, day));
    }

    return false;
  };

  useEffect(() => {
    if (props.status === 'alert') {
      setIsValid(false);
    } else if (props.status === null) {
      setIsValid(true);
    }
  }, [props.status]);

  const showResetButton =
    typeof props.enableResetButton === 'boolean'
      ? props.enableResetButton
      : props.enableResetButton === undefined && !props.editable;

  return props.readOnly ? (
    <div
      className={cx(cn.container, props.className)}
      style={props.style}
      // NOTE: tabIndex={-1} focuses this div (instead of body) when the icon button is pressed
      //     | in browsers that don't treat buttons as focusable (Safari, FF on MacOS).
      tabIndex={-1}
      data-testid={props.testId}
      role="group"
      aria-roledescription={roleDescription}
      aria-describedby={ariaDescribedBy.join(' ')}
      aria-disabled={props.disabled}
    >
      <Input
        testId={postfixTestId(props.testId, 'input')}
        ref={inputRef}
        id={props.id}
        style={props.style}
        className={cx(cn.dateInput)}
        value={props.value ? (props.format ?? defaultFormat)(props.value) : ''}
        required={props.required}
        editable={false}
        disabled={props.disabled}
        message={
          !isMinMaxValid
            ? props.message ?? printMinMaxErrorMessage
            : props.message ??
              localize(props.status === 'warning' ? 'warning.msg' : 'required.error.msg')
        }
        status={props.status ?? props.required ? textValidator.errors?.status : undefined}
        ariaLabel={props.ariaLabel}
        ariaLabelledBy={props.ariaLabelledBy}
        onChange={() => {}}
        commitOnEnterInDialog
      />
    </div>
  ) : (
    <div
      className={cx(cn.container, props.className)}
      style={props.style}
      // NOTE: tabIndex={-1} focuses this div (instead of body) when the icon button is pressed
      //     | in browsers that don't treat buttons as focusable (Safari, FF on MacOS).
      tabIndex={-1}
      onFocus={() => {
        if (props.disabled) inputRef.current?.setAttribute('aria-roledescription', roleDescription);
        if (refFocusTimeout.current !== undefined) {
          clearTimeout(refFocusTimeout.current);
          refFocusTimeout.current = undefined;
        } else {
          props.onFocusIn?.();
        }
      }}
      onBlur={(event) => {
        if (!props.editable) handleBlur();

        // NOTE: document.activeElement is usually document.body during a blur event. However,
        // it will be equal to the event.target when a user switches away from the browser window or tab.
        // This works on Chrome,FF,Safari,EdgeChrome. If it does break, consider using the Page Visibility API.
        if (refFocusTimeout.current !== undefined || event.target === document.activeElement) {
          return;
        }
        // If the Calendar popup is open, we don't want to fire prop.onFocusOut
        if (!hidePicker) {
          return;
        }
        refFocusTimeout.current = setTimeout(() => {
          props.onFocusOut?.(refPicker.current?.isValid());
          refFocusTimeout.current = undefined;
        });
      }}
      data-testid={props.testId}
      role="group"
      aria-roledescription={roleDescription}
      aria-describedby={ariaDescribedBy.join(' ')}
      aria-disabled={props.disabled}
    >
      {!props.disabled ? (
        <InvisibleText id={invisibleTextId}>
          {localize(props.editable ? 'edit.aria' : 'nonedit.aria')}
        </InvisibleText>
      ) : null}
      <BaseInput
        testId={postfixTestId(props.testId, 'input')}
        ref={inputRef}
        style={props.style}
        id={props.id}
        value={
          props.editable ? value : props.value ? (props.format ?? defaultFormat)(props.value) : ''
        }
        showClearButton={showResetButton}
        clearButtonTooltip={localize('date.resetButton.tooltip.label')}
        clearButtonAriaLabel={localize('date.resetButton.tooltip.label')}
        required={props.required}
        editable={props.editable === true ? true : false}
        variant={'composite'}
        onChange={handleChange}
        onCommit={props.editable ? handleCommit : () => {}}
        disabled={props.disabled}
        message={
          !isMinMaxValid
            ? props.message ?? printMinMaxErrorMessage
            : props.message ??
              localize(props.status === 'warning' ? 'warning.msg' : 'required.error.msg')
        }
        status={
          props.status
            ? props.status
            : props.required
            ? !isValid || !isMinMaxValid
              ? 'alert'
              : textValidator.errors?.status
            : !props.showHoursDuration && !props.showMinutesDuration && (!isValid || !isMinMaxValid)
            ? 'alert'
            : undefined
        }
        ariaLabel={props.ariaLabel}
        ariaLabelledBy={props.ariaLabelledBy}
        commitOnEnterInDialog={props.editable !== true}
        placeholder={props.inputPlaceholder}
      />
      <Button
        className={cx(cn.iconButton, { [cn.editableIconButton]: props.editable })}
        ref={buttonRef}
        icon={{ icon: buttonIcon, tooltip: iconTooltip }}
        disabled={props.disabled}
        onPress={() => {
          setHidePicker((prev) => !prev);
        }}
        focusable={false}
        testId={postfixTestId(props.testId, 'iconButton')}
      />
      <DatePickerPopover
        {...props}
        isOpen={!hidePicker && !props.disabled}
        value={state}
        onChange={({ value }) => {
          setTimeout(() => {
            if (!mountedRef.current) return;
            const newDate = value ?? new Date();
            setState(newDate);
          }, 0);
        }}
        onEscape={() => {
          setHidePicker(true);
          setState(props.value ?? new Date());
          // NOTE: The focus lock component is used by the Popover when modal and
          // focus lock events seem to hijack focus after this event fires off.
          // Have to have focus events trigger as the last event in the stack and
          // calling the focus inside of a setTimeout with a delay of 0 does the trick.
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        onOkPress={() => {
          setHidePicker(true);
          if (!isDisabledDate(state)) {
            const value = new Date(state);
            dispatchFocusAction({ type: 'selection', value });
            props.onChange({ value: state });
            setValue((props.format ?? defaultFormat)(state));
            if (props.required) handleChange({ value: (props.format ?? defaultFormat)(state) });
            setIsValid(true);
            setIsMinMaxValid(true);
          }
          // NOTE: The focus lock component is used by the Popover when modal and
          // focus lock events seem to hijack focus after this event fires off.
          // Have to have focus events trigger as the last event in the stack and
          // calling the focus inside of a setTimeout with a delay of 0 does the trick.
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        onCancelPress={() => {
          setHidePicker(true);
          setState(props.value ?? new Date());
          // NOTE: The focus lock component is used by the Popover when modal and
          // focus lock events seem to hijack focus after this event fires off.
          // Have to have focus events trigger as the last event in the stack and
          // calling the focus inside of a setTimeout with a delay of 0 does the trick.
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        onTodayButtonPress={(date) => {
          if (type !== 'date') return;
          setHidePicker(true);
          if (!isDisabledDate(date)) {
            const value = new Date(date);
            dispatchFocusAction({ type: 'selection', value });
            props.onChange({ value: date });
            setValue((props.format ?? defaultFormat)(date));
            if (props.required) handleChange({ value: (props.format ?? defaultFormat)(date) });
            setIsValid(true);
            setIsMinMaxValid(true);
          }
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        referenceElementRef={buttonRef}
      />
    </div>
  );
});

export const DatePickerWithLabels = withLabels(DatePicker);
